import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { GET_SELLER_ORDER_STATUS_COUNTS } from "../../../../Utils/Queries";

const ORDER_STATUS_FALLBACK = [
  { name: "New Product", value: 22, color: "#8979FF" },
  { name: "Processing", value: 18, color: "#FF928A" },
  { name: "Delivered", value: 24, color: "#3CC3DF" },
  { name: "Cancelled", value: 26, color: "#DC2626" },
  { name: "Out for Delivery", value: 10, color: "#B923E1" },
];

/** Same slice order, labels, and colors as the static chart; maps seller API statuses. */
const STATUS_TO_SLICE = {
  new_order: "New Product",
  processing: "Processing",
  ready_pickup: "Processing",
  delivered: "Delivered",
  cancelled: "Cancelled",
  failed_delivery: "Cancelled",
  out_delivery: "Out for Delivery",
};

const SLICE_ORDER = [
  { name: "New Product", color: "#8979FF" },
  { name: "Processing", color: "#FF928A" },
  { name: "Delivered", color: "#3CC3DF" },
  { name: "Cancelled", color: "#DC2626" },
  { name: "Out for Delivery", color: "#B923E1" },
];

function buildOrderStatusChartRows(items) {
  if (!items?.length) return null;
  const counts = Object.fromEntries(SLICE_ORDER.map((s) => [s.name, 0]));
  for (const row of items) {
    const key = String(row.status ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    const sliceName = STATUS_TO_SLICE[key];
    if (sliceName) counts[sliceName] += Number(row.count) || 0;
    else counts["Processing"] += Number(row.count) || 0;
  }
  return SLICE_ORDER.map((s) => ({
    name: s.name,
    value: counts[s.name],
    color: s.color,
  }));
}

export const OrderStatusCard = () => {
  const { data } = useQuery(GET_SELLER_ORDER_STATUS_COUNTS);
  const rows = data?.getSellerOrderStatusCounts;

  const orderStatusData = useMemo(() => {
    const fromApi = buildOrderStatusChartRows(rows);
    return fromApi ?? ORDER_STATUS_FALLBACK;
  }, [rows]);

  const total = orderStatusData.reduce((acc, cur) => acc + cur.value, 0);
  return (
    <div className="bg-white rounded-[14px] border border-[#EFEFEF] p-5 h-[400px]">
      <div>
        <h3 className="text-[15px] font-semibold text-[#3D3D3D] my-0">
          Order Status
        </h3>
        <p className="text-[12.5px] text-[#626262] mt-1 my-0">Current period</p>
      </div>

      <div className="relative w-full h-[250px] mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={orderStatusData}
              dataKey="value"
              nameKey="name"
              innerRadius={60}
              outerRadius={95}
              paddingAngle={0}
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              {orderStatusData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className="text-[20px] font-black my-0 mt-3 text-[#3D3D3D] leading-none">
            {total.toLocaleString()}
          </p>
          <p className="text-[12px] text-[#6B7280] mt-1">Orders</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-4 px-2">
        {orderStatusData.map((s) => (
          <div key={s.name} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full inline-block"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-[12px] text-[#4B5563]">{s.name}</span>
          </div>
        ))}
      </div>
      {/* total reference for accessibility */}
      <span className="sr-only">Total orders {total}</span>
    </div>
  );
};